import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
  setOpen: any
  setHistoryOpen: any
  setCommentsOpen: any
  setTranscript_list: any
  handleMenuClick: any
  getColumnSearchProps: any
  setFilterGeneOpen: any
  setMenuType: any
  setFilterGeneData: any
  getFilterGene: any
  setIndexOpen: any
  setGeneIndexOpen: any
  setGeneOpen: any
  setAcmgOpen: any
  setOMIMOpen: any
  setIgvOpen: any
  setIgvLocus: any
  setCnvScipOpen: any
  setCnvAllOpen: any
  VariationGeneRef: any
  analysisInfo: any
  query: any
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1CaseControl = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    return [
      {
        accessorKey: 'genes',
        id: 'genes',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            {/* <Filters keys="gene" /> */}

            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {`${t('Gene')} ID`}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Tooltip
                title={row.original.genes ? row.original.genes.join('/') : ''}
                placement="top-start"
              >
                <TypographyA
                  sx={{
                    color: '#23527c',
                    fontWeight: '700',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.genes ? row.original.genes.join('/') : ''}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'term',
        id: 'term',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Description')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.term}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'gene_ratio',
        id: 'gene_ratio',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('GR')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.gene_ratio
                  ? row.original.gene_ratio.toFixed(4)
                  : row.original.gene_ratio}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'odds_ratio',
        id: 'odds_ratio',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('OR')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.odds_ratio
                  ? row.original.odds_ratio.toFixed(4)
                  : row.original.odds_ratio}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'hits_ratio',
        id: 'hits_ratio',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('HR')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.hits_ratio
                  ? Number(row.original.hits_ratio).toFixed(4)
                  : '.'}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'p_value',
        id: 'p_value',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('P-Value')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.p_value
                  ? Number(row.original.p_value).toFixed(4)
                  : '.'}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'adjusted_p_value',
        id: 'adjusted_p_value',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('p.adjust')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.adjusted_p_value
                  ? Number(row.original.adjusted_p_value).toFixed(4)
                  : '.'}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'count',
        id: 'count',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Count')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <TypographyA
                sx={{
                  color: '#23527c',
                  fontWeight: '700'
                }}
              >
                {row.original.count}
              </TypographyA>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      }
    ]
  }, [props])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1CaseControl
